import type { McpClient } from "./mcpClients";
import { MCP_SERVER_URL, MCP_TOOL_COUNT } from "./mcpServer";

const SITE_ORIGIN = "https://flowstep.ai";

// TanStack's head meta type does not model ld+json entries, which the router
// serialises into <script type="application/ld+json">. Same cast the homepage
// route uses.
type HeadMeta = React.JSX.IntrinsicElements["meta"][];

const breadcrumbJsonLd = (
  trail: { name: string; path: string }[],
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: trail.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: `${SITE_ORIGIN}${crumb.path}`,
  })),
});

const faqJsonLd = (
  faqs: { question: string; answer: string }[],
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});

/**
 * Shared page head: title, description and the social tags that override the
 * site-wide defaults in __root.
 *
 * Canonical and og:url are deliberately absent. RootDocument derives both
 * from the current pathname for every route, and adding them here emits a
 * second, identical tag.
 */
const baseHead = ({
  title,
  description,
  jsonLd,
  image,
}: {
  title: string;
  description: string;
  jsonLd: Record<string, unknown>[];
  /** Page-specific social card under /og/, overriding the site default. */
  image?: { path: string; alt: string };
}) => ({
  meta: [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    ...(image
      ? [
          { property: "og:image", content: `${SITE_ORIGIN}${image.path}` },
          {
            property: "og:image:secure_url",
            content: `${SITE_ORIGIN}${image.path}`,
          },
          { property: "og:image:alt", content: image.alt },
          { name: "twitter:image", content: `${SITE_ORIGIN}${image.path}` },
          { name: "twitter:image:alt", content: image.alt },
        ]
      : []),
    ...jsonLd.map((entry) => ({ "script:ld+json": entry })),
  ] as unknown as HeadMeta,
});

export const mcpClientHead = (client: McpClient) => {
  const path = `/mcp/${client.slug}`;

  return baseHead({
    title: client.metaTitle,
    description: client.metaDescription,
    image: {
      path: `/og/mcp/${client.slug}.png`,
      alt: `Flowstep MCP for ${client.name}`,
    },
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Flowstep", path: "/" },
        { name: "MCP", path: "/mcp" },
        { name: client.name, path },
      ]),
      faqJsonLd(client.faqs),
    ],
  });
};

/** Head for the MCP article pages (comparison, list, definition). */
export const mcpArticleHead = ({
  title,
  description,
  path,
  breadcrumbName,
  faqs,
}: {
  title: string;
  description: string;
  path: string;
  breadcrumbName: string;
  faqs: { question: string; answer: string }[];
}) =>
  baseHead({
    title,
    description,
    image: { path: `/og${path}.png`, alt: title },
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Flowstep", path: "/" },
        { name: "MCP", path: "/mcp" },
        { name: breadcrumbName, path },
      ]),
      faqJsonLd(faqs),
    ],
  });

export const mcpHubHead = ({
  title,
  description,
  clients,
  faqs,
}: {
  title: string;
  description: string;
  clients: McpClient[];
  faqs: { question: string; answer: string }[];
}) => {
  const softwareJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Flowstep MCP server",
    url: `${SITE_ORIGIN}/mcp`,
    applicationCategory: "DeveloperApplication",
    operatingSystem: "Web",
    description: `A remote Model Context Protocol server at ${MCP_SERVER_URL} that exposes ${MCP_TOOL_COUNT} Flowstep tools to MCP clients, so AI coding agents can generate, preview and edit real UI designs.`,
    isAccessibleForFree: true,
    provider: {
      "@type": "Organization",
      name: "Flowstep",
      url: SITE_ORIGIN,
    },
  };

  const clientListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "MCP clients that work with Flowstep",
    itemListElement: clients.map((client, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: `Flowstep MCP for ${client.name}`,
      url: `${SITE_ORIGIN}/mcp/${client.slug}`,
    })),
  };

  return baseHead({
    title,
    description,
    image: { path: "/og/mcp/hub.png", alt: "The Flowstep MCP server" },
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Flowstep", path: "/" },
        { name: "MCP", path: "/mcp" },
      ]),
      softwareJsonLd,
      clientListJsonLd,
      faqJsonLd(faqs),
    ],
  });
};
